import React, { useState, useMemo } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Stack,
  Button,
  Chip,
  Avatar,
  Paper,
  Grid,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Divider,
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { CiSearch } from 'react-icons/ci';
import { useHeaderBreadcrumbs } from 'src/context/header/HeaderContext';
import {
  IoArrowBack,
  IoPencilOutline,
  IoTrashOutline,
  IoDownloadOutline,
  IoSchoolOutline,
  IoArrowForward,
  IoChevronForward,
} from 'react-icons/io5';
import {
  FaUserGraduate,
  FaChalkboardTeacher,
  FaCoins,
  FaIdCard,
  FaEnvelope,
} from 'react-icons/fa';
import { MdOutlineAccountBalanceWallet } from 'react-icons/md';

import Page from 'src/components/Page';
import { teacherService } from 'src/services/teacher.service';
import { cohortService } from 'src/services/cohort.service';
import { CohortCard } from 'src/components/cohorts/CohortCard';

export default function ViewTeacher() {
  const { teacherId } = useParams<{ teacherId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState<number>(0);
  const [scholarSearch, setScholarSearch] = useState<string>('');

  // 1. Fetch Teacher Info
  const {
    data: teacher,
    isLoading: isLoadingTeacher,
    error: teacherError,
  } = useQuery({
    queryKey: ['teacher-detail', teacherId],
    queryFn: () => teacherService.getTeacherById(teacherId || ''),
    enabled: !!teacherId,
  });

  // 2. Fetch All Cohorts to find ones assigned to this teacher
  const { data: allCohorts = [], isLoading: isLoadingCohorts } = useQuery({
    queryKey: ['cohorts-list'],
    queryFn: () => cohortService.getCohorts(),
  });

  // Filter cohorts assigned to this teacher (matches by teacher.id or user.email/userid)
  const teacherCohorts = useMemo(() => {
    if (!teacher || !allCohorts) return [];
    return allCohorts.filter((c) =>
      (c.teachers || []).some(
        (t) =>
          t.teacherId === teacher.id ||
          t.email?.toLowerCase() === teacher.user?.email?.toLowerCase() ||
          t.id === teacher.id
      )
    );
  }, [teacher, allCohorts]);

  // Aggregate all unique students taught by this teacher across their cohorts
  const teacherStudents = useMemo(() => {
    const studentMap = new Map<string, any>();
    teacherCohorts.forEach((c) => {
      (c.students || []).forEach((s) => {
        if (!studentMap.has(s.studentId)) {
          studentMap.set(s.studentId, {
            ...s,
            cohortName: c.name,
            cohortId: c.id,
            className: c.className,
          });
        }
      });
    });
    return Array.from(studentMap.values());
  }, [teacherCohorts]);

  // Financial and size aggregations
  const totalScholars = teacherStudents.length;
  const totalSupervisedVault = teacherStudents.reduce((sum, s) => sum + (s.balance || 0), 0);
  const avgScholarBalance = totalScholars > 0 ? Math.round(totalSupervisedVault / totalScholars) : 0;

  // Filtered scholar list in Tab 1
  const filteredScholars = useMemo(() => {
    if (!scholarSearch.trim()) return teacherStudents;
    const q = scholarSearch.toLowerCase();
    return teacherStudents.filter(
      (s) =>
        `${s.firstname} ${s.lastname}`.toLowerCase().includes(q) ||
        s.email?.toLowerCase().includes(q) ||
        s.cohortName?.toLowerCase().includes(q) ||
        s.grade?.toLowerCase().includes(q)
    );
  }, [teacherStudents, scholarSearch]);

  // Delete mutation
  const deleteTeacherMutation = useMutation({
    mutationFn: (userId: string) => teacherService.deleteTeacher(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teachers'] });
      toast.success('Teacher staff account deleted successfully.');
      navigate('/admin/teachers');
    },
    onError: (err: any) => {
      toast.error(err?.message || 'Failed to delete teacher.');
    },
  });

  const handleExportRosterCSV = () => {
    if (teacherStudents.length === 0) {
      toast.info('No scholars to export.');
      return;
    }
    const headers = ['First Name', 'Last Name', 'Email', 'Grade', 'Cohort Section', 'Balance'];
    const rows = teacherStudents.map((s) => [
      s.firstname,
      s.lastname,
      s.email,
      s.grade,
      s.cohortName,
      (s.balance || 0).toString(),
    ]);
    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute(
      'download',
      `teacher_${teacher?.user?.firstname || 'staff'}_${teacher?.user?.lastname || 'roster'}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Scholar roster CSV downloaded successfully!');
  };

  const user = teacher?.user;
  const fullName = `${user?.firstname || ''} ${user?.lastname || ''}`.trim() || 'Educator Profile';
  const subjectName = teacher?.subject || 'Financial Literacy & Leadership';
  const staffId = user?.extendedproperties?.teacherId || teacher?.id?.slice(0, 8);

  useHeaderBreadcrumbs(
    teacher
      ? {
          backUrl: '/admin/teachers',
          backLabel: 'Teachers',
          breadcrumbs: [
            { label: 'Teachers Directory', path: '/admin/teachers' },
            { label: fullName },
          ],
        }
      : null
  );

  if (isLoadingTeacher || isLoadingCohorts) {
    return (
      <Page title="Educator Profile | Gold Minds Portal">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress sx={{ color: '#EDB42E' }} />
        </Box>
      </Page>
    );
  }

  if (teacherError || !teacher) {
    return (
      <Page title="Educator Not Found | Gold Minds Portal">
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Educator profile could not be found or may have been deleted.
          </Alert>
          <Button
            variant="contained"
            size="small"
            startIcon={<IoArrowBack />}
            onClick={() => navigate('/admin/teachers')}
            sx={{ bgcolor: '#EDB42E', color: '#000', fontWeight: 700 }}
          >
            Back to Teachers Directory
          </Button>
        </Box>
      </Page>
    );
  }

  return (
    <Page title={`${fullName} | Educator Profile`}>
      <Box sx={{ width: '100%', px: { xs: 0.75, md: 1.25 }, pt: 0, pb: 1.5 }}>
        {/* Hero Section Header Card */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 2.25 },
            mb: 1.5,
            borderRadius: 2.5,
            border: '1px solid #E2E8F0',
            bgcolor: '#FFFFFF',
            position: 'relative',
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            spacing={2}
          >
            {/* Avatar and Basic Details */}
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar
                sx={{
                  width: { xs: 52, md: 64 },
                  height: { xs: 52, md: 64 },
                  bgcolor: '#FEF3C7',
                  color: '#D97706',
                  fontWeight: 800,
                  fontSize: { xs: '1.25rem', md: '1.5rem' },
                  border: '2px solid #FDE68A',
                }}
              >
                {user?.firstname?.[0] || 'T'}
              </Avatar>

              <Box>
                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 0.25 }}>
                  <Typography variant="h5" fontWeight={800} color="#0F172A">
                    {fullName}
                  </Typography>
                  <Chip
                    label={subjectName}
                    size="small"
                    sx={{
                      bgcolor: '#EFF6FF',
                      color: '#1D4ED8',
                      fontWeight: 700,
                      fontSize: '0.725rem',
                      borderRadius: 1.5,
                      border: '1px solid #DBEAFE',
                      height: 22,
                    }}
                  />
                  <Chip
                    label={`Staff ID: ${staffId}`}
                    size="small"
                    sx={{
                      bgcolor: '#F1F5F9',
                      color: '#475569',
                      fontWeight: 700,
                      fontSize: '0.725rem',
                      borderRadius: 1.5,
                      height: 22,
                    }}
                  />
                </Stack>

                <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <FaEnvelope size={12} color="#64748B" />
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      {user?.email || 'No email registered'}
                    </Typography>
                  </Stack>
                  <Typography variant="caption" color="#059669" fontWeight={700}>
                    • Active Faculty Member
                  </Typography>
                </Stack>
              </Box>
            </Stack>

            {/* Quick Actions */}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                variant="outlined"
                size="small"
                startIcon={<IoPencilOutline size={15} />}
                onClick={() => navigate(`/admin/edit-teacher/${teacher.id}`)}
                sx={{
                  borderRadius: 1.75,
                  borderColor: '#CBD5E0',
                  color: '#334155',
                  fontWeight: 700,
                  fontSize: '0.78125rem',
                  textTransform: 'none',
                  py: 0.5,
                  '&:hover': { bgcolor: '#F8FAFC', borderColor: '#94A3B8' },
                }}
              >
                Edit Staff Account
              </Button>

              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<IoTrashOutline size={15} />}
                onClick={() => {
                  if (
                    window.confirm(
                      `Are you sure you want to delete educator ${fullName}? This will remove them from assigned sections.`
                    )
                  ) {
                    deleteTeacherMutation.mutate(user?.id || teacher.id);
                  }
                }}
                sx={{
                  borderRadius: 1.75,
                  borderColor: '#FECACA',
                  color: '#DC2626',
                  fontWeight: 700,
                  fontSize: '0.78125rem',
                  textTransform: 'none',
                  py: 0.5,
                  '&:hover': { bgcolor: '#FEF2F2', borderColor: '#EF4444' },
                }}
              >
                Delete
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {/* 4 Performance & Reach KPI Tiles */}
        <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
          <Grid item xs={6} sm={3}>
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: '1px solid #E2E8F0',
                bgcolor: '#FFFFFF',
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
              }}
            >
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: 1.5,
                  bgcolor: '#EFF6FF',
                  color: '#2563EB',
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                <IoSchoolOutline size={18} />
              </Box>
              <Box>
                <Typography variant="caption" fontWeight={700} color="text.secondary" fontSize="0.65rem" letterSpacing={0.3}>
                  ASSIGNED SECTIONS
                </Typography>
                <Typography variant="subtitle1" fontWeight={800} color="#0F172A" lineHeight={1.1}>
                  {teacherCohorts.length}
                </Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: '1px solid #E2E8F0',
                bgcolor: '#FFFFFF',
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
              }}
            >
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: 1.5,
                  bgcolor: '#ECFDF5',
                  color: '#059669',
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                <FaUserGraduate size={16} />
              </Box>
              <Box>
                <Typography variant="caption" fontWeight={700} color="text.secondary" fontSize="0.65rem" letterSpacing={0.3}>
                  SCHOLARS TAUGHT
                </Typography>
                <Typography variant="subtitle1" fontWeight={800} color="#059669" lineHeight={1.1}>
                  {totalScholars}
                </Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: '1px solid #E2E8F0',
                bgcolor: '#FFFFFF',
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
              }}
            >
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: 1.5,
                  bgcolor: '#FEF3C7',
                  color: '#D97706',
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                <MdOutlineAccountBalanceWallet size={18} />
              </Box>
              <Box>
                <Typography variant="caption" fontWeight={700} color="text.secondary" fontSize="0.65rem" letterSpacing={0.3}>
                  SUPERVISED VAULT
                </Typography>
                <Typography variant="subtitle1" fontWeight={800} color="#0F172A" lineHeight={1.1}>
                  ${totalSupervisedVault.toLocaleString()}
                </Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: '1px solid #E2E8F0',
                bgcolor: '#FFFFFF',
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
              }}
            >
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: 1.5,
                  bgcolor: '#F3E8FF',
                  color: '#7C3AED',
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                <FaCoins size={16} />
              </Box>
              <Box>
                <Typography variant="caption" fontWeight={700} color="text.secondary" fontSize="0.65rem" letterSpacing={0.3}>
                  AVG SCHOLAR WEALTH
                </Typography>
                <Typography variant="subtitle1" fontWeight={800} color="#0F172A" lineHeight={1.1}>
                  ${avgScholarBalance.toLocaleString()}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Tabbed Navigation Workspace */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2.5,
            border: '1px solid #E2E8F0',
            bgcolor: '#FFFFFF',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ borderBottom: 1, borderColor: '#E2E8F0', px: 2, pt: 0.5, bgcolor: '#FAFAFA' }}>
            <Tabs
              value={activeTab}
              onChange={(_, newValue) => setActiveTab(newValue)}
              textColor="primary"
              indicatorColor="primary"
              sx={{
                minHeight: 42,
                '& .MuiTab-root': {
                  fontWeight: 700,
                  fontSize: '0.8125rem',
                  textTransform: 'none',
                  minHeight: 42,
                  px: 1.75,
                  py: 0.5,
                },
              }}
            >
              <Tab label={`Assigned Sections & Cohorts (${teacherCohorts.length})`} />
              <Tab label={`Enrolled Scholars Roster (${teacherStudents.length})`} />
              <Tab label="Faculty Account Details" />
            </Tabs>
          </Box>

          {/* TAB 0: Assigned Cohorts & Sections */}
          {activeTab === 0 && (
            <Box sx={{ p: { xs: 1.5, md: 2 } }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={800} color="#0F172A">
                    Assigned Grade Cohorts ({teacherCohorts.length})
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Sections and classrooms instructed and managed by {fullName}.
                  </Typography>
                </Box>

                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate('/admin/cohorts')}
                  sx={{
                    borderRadius: 1.5,
                    borderColor: '#CBD5E0',
                    color: '#334155',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    textTransform: 'none',
                    '&:hover': { bgcolor: '#F8FAFC' },
                  }}
                >
                  Browse All Cohorts Hub →
                </Button>
              </Stack>

              {teacherCohorts.length === 0 ? (
                <Paper
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    borderRadius: 2,
                    border: '1px dashed #CBD5E0',
                    bgcolor: '#F8FAFC',
                  }}
                >
                  <FaChalkboardTeacher size={36} color="#94A3B8" />
                  <Typography variant="subtitle2" fontWeight={700} color="#334155" mt={1}>
                    No Sections Assigned Yet
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                    Assign {fullName} to grade sections and cohorts from the Cohorts Hub.
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => navigate('/admin/cohorts')}
                    sx={{ bgcolor: '#EDB42E', color: '#000000', fontWeight: 700, fontSize: '0.75rem' }}
                  >
                    Go to Cohorts Hub
                  </Button>
                </Paper>
              ) : (
                <Grid container spacing={1.75}>
                  {teacherCohorts.map((cohort) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={cohort.id}>
                      <CohortCard cohort={cohort} />
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          )}

          {/* TAB 1: Enrolled Scholars Roster */}
          {activeTab === 1 && (
            <Box sx={{ p: { xs: 1.5, md: 2 } }}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', md: 'center' }}
                spacing={1.5}
                sx={{ mb: 1.75 }}
              >
                <Stack direction="row" spacing={1.25} alignItems="center" sx={{ flex: 1 }}>
                  <TextField
                    placeholder="Search scholar by name, email, or section..."
                    size="small"
                    value={scholarSearch}
                    onChange={(e) => setScholarSearch(e.target.value)}
                    sx={{ maxWidth: 360, bgcolor: '#FFFFFF' }}
                    InputProps={{
                      sx: { height: 32, fontSize: '0.8125rem' },
                      startAdornment: (
                        <InputAdornment position="start">
                          <CiSearch size={16} color="#64748B" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" fontWeight={600} fontSize="0.75rem">
                    Showing {filteredScholars.length} of {teacherStudents.length}
                  </Typography>
                </Stack>

                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<IoDownloadOutline size={14} />}
                  onClick={handleExportRosterCSV}
                  sx={{
                    borderRadius: 1.5,
                    borderColor: '#CBD5E0',
                    color: '#334155',
                    fontWeight: 700,
                    fontSize: '0.725rem',
                    py: 0.4,
                    px: 1.25,
                    textTransform: 'none',
                    '&:hover': { bgcolor: '#F8FAFC' },
                  }}
                >
                  Export Roster CSV
                </Button>
              </Stack>

              {filteredScholars.length === 0 ? (
                <Paper
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    borderRadius: 2,
                    border: '1px dashed #CBD5E0',
                    bgcolor: '#F8FAFC',
                  }}
                >
                  <FaUserGraduate size={32} color="#94A3B8" />
                  <Typography variant="subtitle2" fontWeight={700} color="#334155" mt={1}>
                    {scholarSearch ? 'No matching scholars found' : 'No Scholars Enrolled Under This Educator'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {scholarSearch
                      ? 'Try adjusting your search query.'
                      : 'Assign cohorts to this educator to view their combined student roster.'}
                  </Typography>
                </Paper>
              ) : (
                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 800, color: '#334155', fontSize: '0.725rem', py: 0.75 }}>
                          SCHOLAR
                        </TableCell>
                        <TableCell sx={{ fontWeight: 800, color: '#334155', fontSize: '0.725rem', py: 0.75 }}>
                          EMAIL ADDRESS
                        </TableCell>
                        <TableCell sx={{ fontWeight: 800, color: '#334155', fontSize: '0.725rem', py: 0.75 }}>
                          SECTION / COHORT
                        </TableCell>
                        <TableCell sx={{ fontWeight: 800, color: '#334155', fontSize: '0.725rem', py: 0.75 }}>
                          GRADE LEVEL
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800, color: '#334155', fontSize: '0.725rem', py: 0.75 }}>
                          CURRENT BALANCE
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800, color: '#334155', fontSize: '0.725rem', py: 0.75 }}>
                          ACTION
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredScholars.map((student) => (
                        <TableRow
                          key={student.id || student.studentId}
                          hover
                          onClick={() => navigate(`/admin/view-student/${student.studentId || student.id}`)}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': { bgcolor: '#F8FAFC' },
                            transition: 'background-color 0.15s',
                          }}
                        >
                          {/* Scholar */}
                          <TableCell sx={{ py: 0.75 }}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Avatar
                                sx={{
                                  width: 28,
                                  height: 28,
                                  bgcolor: '#DBEAFE',
                                  color: '#1E40AF',
                                  fontSize: '0.75rem',
                                  fontWeight: 700,
                                }}
                              >
                                {student.firstname?.[0]}
                              </Avatar>
                              <Box>
                                <Typography
                                  variant="subtitle2"
                                  fontWeight={700}
                                  color="#0F172A"
                                  fontSize="0.8125rem"
                                  sx={{ '&:hover': { color: '#2563EB', textDecoration: 'underline' } }}
                                >
                                  {student.firstname} {student.lastname}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" fontSize="0.6875rem">
                                  ID: {student.studentId?.slice(0, 8)}...
                                </Typography>
                              </Box>
                            </Stack>
                          </TableCell>

                          {/* Email */}
                          <TableCell sx={{ py: 0.75 }}>
                            <Typography variant="body2" color="text.secondary" fontSize="0.78125rem">
                              {student.email || 'No email registered'}
                            </Typography>
                          </TableCell>

                          {/* Cohort Section */}
                          <TableCell sx={{ py: 0.75 }}>
                            <Chip
                              label={student.cohortName || 'Assigned Section'}
                              size="small"
                              sx={{
                                bgcolor: '#EFF6FF',
                                color: '#1D4ED8',
                                fontWeight: 700,
                                fontSize: '0.7rem',
                                height: 20,
                              }}
                            />
                          </TableCell>

                          {/* Grade */}
                          <TableCell sx={{ py: 0.75 }}>
                            <Chip
                              label={student.grade ? `Grade ${student.grade}` : 'Grade 7'}
                              size="small"
                              sx={{
                                bgcolor: '#F1F5F9',
                                color: '#334155',
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                height: 20,
                              }}
                            />
                          </TableCell>

                          {/* Balance */}
                          <TableCell align="right" sx={{ py: 0.75 }}>
                            <Typography variant="subtitle2" fontWeight={800} color="#059669" fontSize="0.8125rem">
                              ${(student.balance || 0).toLocaleString()}
                            </Typography>
                          </TableCell>

                          {/* Action */}
                          <TableCell align="right" sx={{ py: 0.75 }}>
                            <Button
                              size="small"
                              endIcon={<IoArrowForward size={12} />}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/admin/view-student/${student.studentId || student.id}`);
                              }}
                              sx={{
                                fontSize: '0.7rem',
                                fontWeight: 700,
                                color: '#2563EB',
                                textTransform: 'none',
                                p: 0.25,
                              }}
                            >
                              Profile
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {/* TAB 2: Faculty Account Details */}
          {activeTab === 2 && (
            <Box sx={{ p: { xs: 1.5, md: 2 }, maxWidth: 700 }}>
              <Typography variant="subtitle1" fontWeight={800} color="#0F172A" gutterBottom>
                Faculty Profile & Staff Account
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                Authentication credentials, instructional subject assignments, and portal access role.
              </Typography>

              <Stack spacing={2}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #E2E8F0' }}>
                  <Typography variant="subtitle2" fontWeight={700} color="#0F172A" gutterBottom>
                    Staff Overview
                  </Typography>
                  <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        FULL NAME
                      </Typography>
                      <Typography variant="body2" fontWeight={700}>
                        {fullName}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        PRIMARY SUBJECT
                      </Typography>
                      <Typography variant="body2" fontWeight={700}>
                        {subjectName}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        STAFF / TEACHER ID
                      </Typography>
                      <Typography variant="body2" fontWeight={700}>
                        {staffId}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        EMAIL ADDRESS
                      </Typography>
                      <Typography variant="body2" fontWeight={700}>
                        {user?.email || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        PORTAL ACCESS ROLE
                      </Typography>
                      <Chip
                        label="TEACHER"
                        size="small"
                        sx={{ bgcolor: '#FEF3C7', color: '#92400E', fontWeight: 800, height: 20, fontSize: '0.6875rem' }}
                      />
                    </Grid>
                  </Grid>
                  <Divider sx={{ my: 1.5 }} />
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<IoPencilOutline size={15} />}
                    onClick={() => navigate(`/admin/edit-teacher/${teacher.id}`)}
                    sx={{ borderRadius: 1.5, fontWeight: 700, fontSize: '0.75rem', textTransform: 'none' }}
                  >
                    Edit Faculty Account
                  </Button>
                </Paper>
              </Stack>
            </Box>
          )}
        </Paper>
      </Box>
    </Page>
  );
}
